/**
 * Field signals, the browser's half (v1 ticket 43): uncaught errors, failed `/api/` calls and D's lost WebGL context,
 * kept in memory, grouped per visit and sent in batches to `POST /api/diag/errors`, and the learner's own reports
 * ("Something's wrong here", `views/report-sheet.ts`) to `POST /api/diag/reports`, with a small outbox for a report
 * that could not go.
 *
 * Nothing here carries content: paths are masked (`maskPath`: ids and tokens become `:id`, no query or hash), messages
 * and stacks are scrubbed (`scrub`: no query strings, no long quoted strings, 2 KB at most), and the report's note is
 * the only free text. The automatic sends stop when the ⋯ menu's switch is off (localStorage `li-diag`); a report the
 * learner writes still goes. The pure parts take their storage and clock as arguments, so tests run without a page.
 */
import { isChunkError } from './chunk-reload';

export type DiagKind = 'error' | 'rejection' | 'api' | 'webgl';
export type Look = 'D' | 'B';

export interface DiagEvent {
  kind: DiagKind;
  message: string;
  stack?: string;
  /** The masked screen it happened on. */
  route: string;
  /** How often it happened since it was last sent (in the ring buffer: this visit). */
  count: number;
  /** ISO time it last happened. */
  at: string;
  status?: number;
}

export interface RouteVisit { route: string; at: string }

export type ReportKind = 'problem' | 'idea' | 'question';

export interface ReportBody {
  kind: ReportKind;
  note: string;
  route: string;
  viewport?: { w: number; h: number };
  look?: Look;
  errors?: DiagEvent[];
  routes?: RouteVisit[];
  momentId?: string;
  /** Made once when the report is written, so the server stores a report sent twice once. */
  clientId?: string;
  /**
   * Ticket 68: the learner's own evidence references in the owner bundle's
   * id form (`session:<uuid>`, `attempt:<uuid>`, `event:<uuid>`,
   * `replay:<uuid>`). Only refs the learner actually collected travel; the
   * bodies behind them stay in their own stores, and consent-gated content
   * (generation/artifact records) never attaches. Anything outside the ref
   * grammar is dropped by the server, so non-participant reports keep their
   * existing scope.
   */
  sessionRef?: string;
  attemptRef?: string;
  eventRef?: string;
  replayRef?: string;
}

/** A report waiting on the device: whose it is (the session's owner, as drafts are kept) and when it was saved. */
export interface OutboxEntry {
  owner: string;
  savedAt: string;
  body: ReportBody & { clientId: string };
}

/** Who is signed in on this page: the owner a report is kept for, and whether the server has confirmed them. */
export interface Who {
  owner: string | null;
  confirmed: boolean;
}

/** The last errors and routes kept for a report, and the most events one batch carries. */
export const RING_ERRORS = 20;
export const RING_ROUTES = 10;
/** Batches go at most this often (and on `pagehide`). */
export const SEND_EVERY_MS = 10_000;
/** Reports waiting on the device for a connection, at most. */
export const OUTBOX_MAX = 5;
export const NOTE_MAX = 2000;
/** Another account's report older than this is dropped from the outbox (it can only go once they sign in again). */
export const OUTBOX_KEEP_MS = 30 * 86_400_000;
/** The body of a `keepalive` send (the `pagehide` flush) stays under the browser's 64 KiB, and so does every batch. */
export const BATCH_BYTES_MAX = 60_000;
/** A report's body stays under the server's 128 KB limit. */
export const REPORT_BYTES_MAX = 100_000;
const TEXT_MAX = 2048;
const SWITCH_KEY = 'li-diag';
const OUTBOX_KEY = 'li-diag-outbox';

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const local = (): Store | undefined => {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
};

/** Path segments whose next segment is an id or token (two for a road's ends). */
const ID_AFTER: Record<string, number> = {
  moment: 1, moments: 1, practice: 1, land: 1, district: 1, districts: 1, join: 1, recover: 1, friend: 1, exams: 1,
  invitations: 1, users: 1, shares: 2, road: 2, roads: 2, tutor: 1, devices: 1,
};

/** A path as it may be sent: every id or token `:id`, no query, no hash. */
export function maskPath(path: string): string {
  const bare = path.split(/[?#]/, 1)[0] || '/';
  const parts = bare.split('/');
  let ids = 0;
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (!p) continue;
    if (ids > 0) {
      parts[i] = ':id';
      ids -= 1;
    } else if (ID_AFTER[p]) {
      ids = ID_AFTER[p];
    } else if (/^[A-Za-z0-9_-]{16,}$/.test(p) && /\d/.test(p)) {
      // Anything else that looks like an id (long, with a digit) is one.
      parts[i] = ':id';
    }
  }
  return parts.join('/');
}

/**
 * A quoted string, as the server's `scrub` reads one: `'` only at a word's edge (never the apostrophe of `l'aigua` or
 * `didn't`), `"` closing only before a space, a closing bracket or phrase-ending punctuation (a JSON snippet's inner
 * quotes stay inside it), and backticks, “…” and «…».
 */
const QUOTED = new RegExp([
  String.raw`(?<!\p{L})'(?:[^'\n]|(?<=\p{L})'(?=\p{L}))*'(?!\p{L})`,
  String.raw`(?<![\p{L}\p{N}])"(?:[^"\n]|"(?![\s),;\]]|[.:,](?:\s|$)|$))*"(?=[\s),;\]]|[.:,](?:\s|$)|$)`,
  '`[^`\\n]*`', '“[^”\\n]*”', '«[^»\\n]*»',
].join('|'), 'gu');
/** A short quote kept as is: code, not a word (an identifier with `_`, `.`, `$`, a digit or an inner capital). */
const looksLikeCode = (s: string) =>
  s.length <= 40 && /^[A-Za-z_$][\w$.\-]*$/.test(s) && (/[_.$\d]/.test(s) || /[a-z][A-Z]/.test(s));

/**
 * An error's words as they may be sent: a `JSON.parse` message cut before the text it quotes, no query strings,
 * every quoted string `"…"` unless it is a short one that looks like code, 2 KB at most. The server scrubs again.
 */
export function scrub(text: string): string {
  return text
    .replace(/"[\s\S]*\bis not valid JSON/, '"…" is not valid JSON')
    .replace(/\?[^\s)'"`]*/g, '')
    .replace(QUOTED, (m) => (looksLikeCode(m.slice(1, -1)) ? m : '"…"'))
    .slice(0, TEXT_MAX);
}

/** One bug is one group: its kind, its message and the first line of its stack. */
export function fingerprint(e: Pick<DiagEvent, 'kind' | 'message' | 'stack'>): string {
  const top = (e.stack ?? '').split('\n').map((l) => l.trim()).find((l) => l && l !== e.message) ?? '';
  return `${e.kind}\u0000${e.message}\u0000${top}`;
}

/**
 * A visit's memory: every group seen with its count (for a report, the last `RING_ERRORS` touched), what waits to be
 * sent (each group once, with its count since the last send), and the last `RING_ROUTES` routes.
 */
export class DiagBuffer {
  private seen = new Map<string, DiagEvent>();
  private pending = new Map<string, DiagEvent>();
  private visits: RouteVisit[] = [];

  note(e: Omit<DiagEvent, 'count' | 'at' | 'route'> & { route: string }, now = Date.now()): DiagEvent {
    const ev: DiagEvent = { ...e, message: scrub(e.message), ...(e.stack ? { stack: scrub(e.stack) } : {}), route: maskPath(e.route), count: 1, at: new Date(now).toISOString() };
    const fp = fingerprint(ev);
    const was = this.seen.get(fp);
    const kept = was ? { ...was, route: ev.route, at: ev.at, count: was.count + 1, ...(ev.status ? { status: ev.status } : {}) } : ev;
    // Most recent last, so the ring drops the oldest group.
    this.seen.delete(fp);
    this.seen.set(fp, kept);
    while (this.seen.size > RING_ERRORS) this.seen.delete(this.seen.keys().next().value!);
    const p = this.pending.get(fp);
    this.pending.set(fp, p ? { ...kept, count: p.count + 1 } : { ...kept, count: 1 });
    while (this.pending.size > RING_ERRORS) this.pending.delete(this.pending.keys().next().value!);
    return kept;
  }

  /** A screen was shown; the same one twice in a row is one visit. */
  visit(path: string, now = Date.now()) {
    const route = maskPath(path);
    if (this.visits.at(-1)?.route === route) return;
    this.visits.push({ route, at: new Date(now).toISOString() });
    if (this.visits.length > RING_ROUTES) this.visits.shift();
  }

  errors(): DiagEvent[] {
    return [...this.seen.values()];
  }

  routes(): RouteVisit[] {
    return [...this.visits];
  }

  /** The waiting events, handed over and forgotten (a failed send drops them). */
  take(): DiagEvent[] {
    const out = [...this.pending.values()];
    this.pending.clear();
    return out;
  }

  get waiting(): number {
    return this.pending.size;
  }
}

/** The ⋯ menu's "Send error reports" switch, per device; on unless turned off. */
export function diagOn(store: Store | undefined = local()): boolean {
  try {
    return store?.getItem(SWITCH_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setDiag(on: boolean, store: Store | undefined = local()): void {
  try {
    if (on) store?.removeItem(SWITCH_KEY);
    else store?.setItem(SWITCH_KEY, 'off');
  } catch { /* private mode: the switch holds for this page only */ }
  if (!on) buffer.take();
}

const bytes = (v: unknown) => new TextEncoder().encode(JSON.stringify(v)).length;

/**
 * A body cut to `max` bytes of JSON: the oldest events of `key` dropped first (at least one is kept), then their
 * stacks cut. A body already under it is returned as is.
 */
export function fitBody<T extends object>(body: T, key: 'events' | 'errors', max: number): T {
  let list = ((body as Record<string, unknown>)[key] as DiagEvent[] | undefined) ?? [];
  const at = (l: DiagEvent[]) => ({ ...body, [key]: l }) as T;
  if (!list.length || bytes(body) <= max) return body;
  while (list.length > 1 && bytes(at(list)) > max) list = list.slice(1);
  if (bytes(at(list)) > max) list = list.map(({ stack: _stack, ...e }) => e);
  return at(list);
}

/** A random id for a report; `randomUUID` needs a secure context, so a plain-http LAN test gets random hex. */
export function newClientId(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  return [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/** Reports that could not go, oldest first, whoever wrote them (entries that are not ours are skipped). */
export function outbox(store: Store | undefined = local()): OutboxEntry[] {
  try {
    const v = JSON.parse(store?.getItem(OUTBOX_KEY) ?? '[]');
    if (!Array.isArray(v)) return [];
    return (v as OutboxEntry[]).filter((e) => e && typeof e.owner === 'string' && e.owner && typeof e.savedAt === 'string'
      && e.body && typeof e.body === 'object' && typeof e.body.clientId === 'string');
  } catch {
    return [];
  }
}

/** Writes the outbox, dropping other accounts' reports older than 30 days and keeping the last `OUTBOX_MAX`. */
function saveOutbox(list: OutboxEntry[], store: Store | undefined, owner: string | null, now: number) {
  const kept = list.filter((e) => e.owner === owner || now - Date.parse(e.savedAt) <= OUTBOX_KEEP_MS).slice(-OUTBOX_MAX);
  try {
    if (kept.length) store?.setItem(OUTBOX_KEY, JSON.stringify(kept));
    else store?.removeItem(OUTBOX_KEY);
  } catch { /* storage full or off: the report is lost, as a failed error send is */ }
}

/** A report waits for its owner's next send; past `OUTBOX_MAX` the oldest goes. */
export function toOutbox(body: ReportBody & { clientId: string }, owner: string, store: Store | undefined = local(), now = Date.now()): void {
  const list = outbox(store).filter((e) => e.body.clientId !== body.clientId);
  saveOutbox([...list, { owner, savedAt: new Date(now).toISOString(), body }], store, owner, now);
}

/** Sign-out that deletes the learner's drafts: their waiting reports go too. */
export function dropOutboxOf(owner: string, store: Store | undefined = local(), now = Date.now()): void {
  saveOutbox(outbox(store).filter((e) => e.owner !== owner), store, owner, now);
}

/** Takes one sent (or refused) report out, from a fresh read, so a report saved meanwhile stays. */
function removeFromOutbox(clientId: string, store: Store | undefined, owner: string, now: number) {
  saveOutbox(outbox(store).filter((e) => e.body.clientId !== clientId), store, owner, now);
}

type Fetch = typeof fetch;
/** The page's own fetch, before the hook below wraps it, so a diag send is never itself counted. */
let rawFetch: Fetch = (...a) => globalThis.fetch(...a);

/** A JSON POST; `keepalive` only for the `pagehide` flush, whose body is cut below the browser's 64 KiB. */
const post = (url: string, body: unknown, f: Fetch = rawFetch, keepalive = false) =>
  f(url, { method: 'POST', ...(keepalive ? { keepalive: true } : {}), headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

/** How a report went: sent, kept for later, or refused (the status and body, for `serverErrorText`). */
export type ReportResult = { ok: 'sent' | 'saved' } | { status: number; body?: Record<string, unknown> };

/** Who is signed in; `installDiag` hands the session's answer in. Nobody until then. */
let whoNow: () => Who = () => ({ owner: null, confirmed: false });

/** Flushes run one at a time: a second waits for the first, then reads the outbox afresh. */
let flushing: Promise<void> = Promise.resolve();

/**
 * Sends the signed-in learner's waiting reports, oldest first, until one cannot go; another account's stay. A
 * report the server refused (4xx but 429 and 401) is dropped. Nothing goes until the server has confirmed who is
 * signed in.
 */
export function flushOutbox(f: Fetch = rawFetch, store: Store | undefined = local(), who: Who = whoNow()): Promise<void> {
  const run = flushing.then(() => flushNow(f, store, who));
  flushing = run.catch(() => {});
  return run;
}

async function flushNow(f: Fetch, store: Store | undefined, who: Who): Promise<void> {
  const owner = who.owner;
  if (!owner || !who.confirmed) return;
  const tried = new Set<string>();
  for (;;) {
    const next = outbox(store).find((e) => e.owner === owner && !tried.has(e.body.clientId));
    if (!next) return;
    tried.add(next.body.clientId);
    let r: Response;
    try {
      r = await post('/api/diag/reports', fitBody(next.body, 'errors', REPORT_BYTES_MAX), f);
    } catch {
      return;
    }
    // Waiting for tomorrow's limit, the session or the server: it stays.
    if (r.status === 429 || r.status === 401 || r.status >= 500) return;
    removeFromOutbox(next.body.clientId, store, owner, Date.now());
  }
}

/**
 * The learner's report: the outbox goes first, then this one; a lost connection or a 5xx keeps it for later, under
 * the signed-in learner. With nobody known on this page it cannot be kept, so the lost connection is thrown.
 */
export async function sendReport(body: ReportBody, f: Fetch = rawFetch, store: Store | undefined = local(), who: Who = whoNow()): Promise<ReportResult> {
  const report = { ...body, clientId: body.clientId ?? newClientId() };
  await flushOutbox(f, store, who);
  const keep = (): ReportResult => {
    if (!who.owner) throw new TypeError('report not sent');
    toOutbox(report, who.owner, store);
    return { ok: 'saved' };
  };
  let r: Response;
  try {
    r = await post('/api/diag/reports', fitBody(report, 'errors', REPORT_BYTES_MAX), f);
  } catch {
    return keep();
  }
  if (r.ok) return { ok: 'sent' };
  if (r.status >= 500) return keep();
  let payload: Record<string, unknown> | undefined;
  try {
    const b = await r.json();
    if (b && typeof b === 'object' && !Array.isArray(b)) payload = b as Record<string, unknown>;
  } catch { /* no body */ }
  return { status: r.status, body: payload };
}

// ---- the page's hooks ----

const buffer = new DiagBuffer();
let look: Look | undefined;
let timer: ReturnType<typeof setTimeout> | undefined;
let lastSent = 0;

const here = () => (typeof location === 'undefined' ? '/' : location.pathname);
const viewport = () => (typeof innerWidth === 'number' ? { w: innerWidth, h: innerHeight } : undefined);

/** The town on screen is drawn by D or B (home and the round's town say so as they mount). */
export function setLook(l: Look | null) {
  look = l ?? undefined;
}

/** A screen was shown (the router's popstate, and the first screen). */
export function noteRoute(path = here()) {
  buffer.visit(path);
}

/** What a report carries along: the recent errors and routes, the masked route, the viewport and the look. */
export function reportContext(): Pick<ReportBody, 'route' | 'viewport' | 'look' | 'errors' | 'routes'> {
  const errors = buffer.errors(), routes = buffer.routes(), vp = viewport();
  return {
    route: maskPath(here()),
    ...(vp ? { viewport: vp } : {}),
    ...(look ? { look } : {}),
    ...(errors.length ? { errors } : {}),
    ...(routes.length ? { routes } : {}),
  };
}

/** Something went wrong: kept for a report, and sent with the next batch unless the switch is off. */
export function noteError(e: { kind: DiagKind; message: string; stack?: string; status?: number }) {
  buffer.note({ ...e, route: here() });
  if (!diagOn()) {
    buffer.take();
    return;
  }
  if (timer === undefined) timer = setTimeout(() => flush(), Math.max(0, lastSent + SEND_EVERY_MS - Date.now()));
}

/**
 * Sends what waits, fire and forget; a failed send drops it (an error log need not be exact). The body is cut to
 * `BATCH_BYTES_MAX` (oldest groups, then stacks); only the `pagehide` flush uses `keepalive`, which that keeps legal.
 */
function flush(keepalive = false) {
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
  if (!buffer.waiting || !diagOn()) {
    buffer.take();
    return;
  }
  lastSent = Date.now();
  const vp = viewport();
  const body = fitBody({ events: buffer.take(), ...(vp ? { viewport: vp } : {}), ...(look ? { look } : {}) }, 'events', BATCH_BYTES_MAX);
  post('/api/diag/errors', body, rawFetch, keepalive).catch(() => {});
}

const asError = (v: unknown): { message: string; stack?: string; status?: number } => {
  if (v instanceof Error) {
    const status = (v as { status?: unknown }).status;
    return { message: `${v.name}: ${v.message}`, stack: v.stack, ...(typeof status === 'number' ? { status } : {}) };
  }
  return { message: typeof v === 'string' ? v : Object.prototype.toString.call(v) };
};

/** A fetch the page gave up on itself (an abort or an `AbortSignal.timeout`): not a failure worth a group. */
export const isAbort = (v: unknown) => v instanceof Error && (v.name === 'AbortError' || v.name === 'TimeoutError');

/** A status worth a group: a 5xx, but not 501 (listen and the scene's audio answer it by design in mock mode). */
export const failedStatus = (status: number) => status >= 500 && status !== 501;

/** Wraps the page's fetch: a same-origin `/api/` call that ends in a failed status or a lost connection is noted. */
function hookFetch() {
  const orig = window.fetch.bind(window);
  rawFetch = orig;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    let path = '';
    try {
      const u = new URL(typeof input === 'string' || input instanceof URL ? String(input) : input.url, location.href);
      if (u.origin === location.origin) path = u.pathname;
    } catch { /* not a URL we watch */ }
    if (!path.startsWith('/api/') || path.startsWith('/api/diag/')) return orig(input, init);
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    try {
      const r = await orig(input, init);
      if (failedStatus(r.status)) noteError({ kind: 'api', message: `${method} ${maskPath(path)} ${r.status}`, status: r.status });
      return r;
    } catch (e) {
      if (!isAbort(e)) noteError({ kind: 'api', message: `${method} ${maskPath(path)} network` });
      throw e;
    }
  };
}

let installed = false;

/**
 * Starts the hooks once (main.ts): errors, rejections, `/api/` failures, route visits and the outbox. `who` says who
 * is signed in (the session), so the outbox sends only that learner's reports.
 */
export function installDiag(opts: { who?: () => Who } = {}) {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  if (opts.who) whoNow = opts.who;
  hookFetch();
  window.addEventListener('error', (e) => {
    const v = e.error ?? e.message;
    if (isChunkError(v)) return;
    noteError({ kind: 'error', ...asError(v) });
  });
  window.addEventListener('unhandledrejection', (e) => {
    if (isChunkError(e.reason) || isAbort(e.reason)) return;
    noteError({ kind: 'rejection', ...asError(e.reason) });
  });
  window.addEventListener('popstate', () => noteRoute());
  window.addEventListener('pagehide', () => flush(true));
  noteRoute();
  // A report that waited for a connection goes once the app has settled.
  setTimeout(() => { void flushOutbox(); }, 5_000);
}
